import AppVersion from "@/enum/AppVersion";
import { getWaybillStatsOverview } from "@/services/api/ewaybill";
import { ShopOverviewResult, getShopOverview } from "@/services/api/order";
import { ShipmentStatsOverview, getShipStatsOverview } from "@/services/api/shipmentStatistics";
import { getInventoryWarningOverview } from "@/services/api/wms";
import { axiosGroupByShopType } from "@/services/http/platformServer";
import GlobalStore from "@/store";
import { concurrentQueue, isEmpty } from "@/utils";
import { groupBy } from "lodash";
import { computed, observable, makeObservable, actionBound } from "mobx";
import moment from "moment";
import React from "react";

const SHOP_INFO_YESTERDAY_STATS_KEY = "shop-info-yesterday-stats";

class ShopInfoStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            shopOverviewResults: observable,
            yesterdayAllocatedQuantity: observable,
            yesterdayCanceledQuantity: observable,
            yesterdayOrderCount: observable,
            yesterdayItemCount: observable,
            yesterdayWaybillCount: observable,
            totalLogisticsWarningOrders: observable,
            totalOnHoldOrders: observable,
            totalRefundingOrders: observable,
            totalPendingShipmentOrders: observable,
            shippingDeadlineOrderCount: observable,
            isAnyShopHasAbnormalOrder: observable,
            inventoryWarningCount: observable,
            anyInventoryThresholdConfigured: observable,
            shipStatLoading: observable,
            waybillStatLoading: observable,
            wmsLoading: observable,
            getShopOverviewLoading: observable,
            load: actionBound,
            reloadShopOverview: actionBound,
            shopIdsList: computed,
            ensureShopSetting: actionBound,
            loadShopOverview: actionBound,
            loadWaybillStatsOverview: actionBound,
            loadShipStatsOverview: actionBound,
            loadWmsOverview: actionBound,
            getCount: actionBound,
        });
    }

    NoStatistics = "未统计";

    shopOverviewResults: ShopOverviewResult[] = [];

    /** 打印的单号数 */
    yesterdayAllocatedQuantity: number = undefined;

    /** 回收的单号数 */
    yesterdayCanceledQuantity: number = undefined;

    /** 发货的订单数 */
    yesterdayOrderCount: number = undefined;

    /** 发货的商品数 */
    yesterdayItemCount: number = undefined;

    /** 发货的单号数 */
    yesterdayWaybillCount: number = undefined;

    /** 全部物流异常订单数 */
    totalLogisticsWarningOrders: number = undefined;

    /** 全部挂起订单数 */
    totalOnHoldOrders: number = undefined;

    /** 全部退款中订单数 */
    totalRefundingOrders: number = undefined;

    /** 全部待发货订单数 */
    totalPendingShipmentOrders: number = undefined;

    /** 全部即将发货超时订单数 */
    shippingDeadlineOrderCount: number = undefined;

    isAnyShopHasAbnormalOrder: boolean = undefined;

    inventoryWarningCount: number = undefined;

    anyInventoryThresholdConfigured: boolean = undefined;

    yesterday = moment().add(-1, "d").format("YYYY-MM-DD");

    shipStatLoading = false;

    waybillStatLoading = false;

    wmsLoading = false;

    getShopOverviewLoading = false;

    async load() {
        this.loadShopOverview();

        this.loadWmsOverview();

        this.loadWaybillStatsOverview();

        this.loadShipStatsOverview();
    }

    getYesterdayStatsFromCache() {
        const itemStr = localStorage.getItem(SHOP_INFO_YESTERDAY_STATS_KEY);
        if (!itemStr) return null;

        try {
            const cache = JSON.parse(itemStr);
            const today = new Date().toLocaleDateString();

            if (cache.date !== today || cache.companyId !== this.globalStore.companyId) {
                localStorage.removeItem(SHOP_INFO_YESTERDAY_STATS_KEY);
                return null;
            }

            return cache.data;
        } catch {
            return null;
        }
    }

    setYesterdayStatsToCache(data) {
        const today = new Date().toLocaleDateString();
        const cache = {
            date: today,
            data,
            companyId: this.globalStore.companyId,
        };

        localStorage.setItem(SHOP_INFO_YESTERDAY_STATS_KEY, JSON.stringify(cache));
    }

    hasYesterdayStatsCacheFields(cache, fields: string[]) {
        if (!cache) return false;

        return fields.every((field) => Object.prototype.hasOwnProperty.call(cache, field));
    }

    async reloadShopOverview() {
        this.shopOverviewResults = [];
        this.totalLogisticsWarningOrders = undefined;
        this.totalOnHoldOrders = undefined;
        this.totalRefundingOrders = undefined;
        this.totalPendingShipmentOrders = undefined;
        this.shippingDeadlineOrderCount = undefined;
        this.isAnyShopHasAbnormalOrder = undefined;

        const shopIds = this.globalStore.allowedShops.map((it) => it.id);
        this.globalStore.getShopSetting(shopIds);

        this.loadShopOverview();
    }

    /** 按节点分组的店铺列表 */
    get shopIdsList() {
        const shopIds = this.globalStore.allowedShops.map((it) => it.id);

        const groupedShopIds = groupBy<number>([...shopIds], (shopId) => {
            const shopType = this.globalStore.shopMapping.get(shopId)!.type;

            return axiosGroupByShopType(shopType);
        });

        const shopIdsList = Object.values(groupedShopIds);

        return shopIdsList;
    }

    async ensureShopSetting() {
        const shopIds = this.globalStore.allowedShops.map((it) => it.id);

        try {
            await this.globalStore.ensureShopSetting(shopIds);
        } catch {}
    }

    async loadShopOverview() {
        try {
            this.getShopOverviewLoading = true;

            let shopOverviewResults: ShopOverviewResult[] = [];

            await concurrentQueue({
                data: this.shopIdsList,
                callback: async (shopIds) => {
                    let results: ShopOverviewResult[];

                    try {
                        results = await getShopOverview(shopIds[0], shopIds.join());
                    } catch {}

                    if (results?.length) {
                        shopOverviewResults.push(...results);

                        shopOverviewResults = shopOverviewResults.sort(
                            (prev, next) => next.totalPendingShipmentOrders - prev.totalPendingShipmentOrders,
                        );

                        this.shopOverviewResults = shopOverviewResults;
                    }
                },
            });

            /** 所有节点获取到数据后再汇总 */
            if (shopOverviewResults.length) {
                const {
                    totalLogisticsWarningOrders,
                    totalOnHoldOrders,
                    totalRefundingOrders,
                    totalPendingShipmentOrders,
                    shippingDeadlineOrderCount,
                    isAnyShopHasAbnormalOrder,
                } = shopOverviewResults.reduce(
                    (total, current) => {
                        if (!isEmpty(current.totalLogisticsWarningOrders)) {
                            if (isEmpty(total.totalLogisticsWarningOrders)) {
                                total.totalLogisticsWarningOrders = 0;
                            }

                            total.totalLogisticsWarningOrders += current.totalLogisticsWarningOrders;
                        }

                        if (!isEmpty(current.totalOnHoldOrders)) {
                            if (isEmpty(total.totalOnHoldOrders)) {
                                total.totalOnHoldOrders = 0;
                            }

                            total.totalOnHoldOrders += current.totalOnHoldOrders;
                        }

                        if (!isEmpty(current.totalRefundingOrders)) {
                            if (isEmpty(total.totalRefundingOrders)) {
                                total.totalRefundingOrders = 0;
                            }

                            total.totalRefundingOrders += current.totalRefundingOrders;
                        }

                        if (!isEmpty(current.totalPendingShipmentOrders)) {
                            if (isEmpty(total.totalPendingShipmentOrders)) {
                                total.totalPendingShipmentOrders = 0;
                            }

                            total.totalPendingShipmentOrders += current.totalPendingShipmentOrders;
                        }

                        if (!isEmpty(current.shippingDeadlineOrderCount)) {
                            if (isEmpty(total.shippingDeadlineOrderCount)) {
                                total.shippingDeadlineOrderCount = 0;
                            }

                            total.shippingDeadlineOrderCount += current.shippingDeadlineOrderCount;
                        }

                        if (!isEmpty(current.hasAbnormalOrders)) {
                            if (current.hasAbnormalOrders) {
                                total.isAnyShopHasAbnormalOrder = true;
                            }
                        }

                        return total;
                    },
                    {
                        totalLogisticsWarningOrders: null,
                        totalOnHoldOrders: null,
                        totalRefundingOrders: null,
                        totalPendingShipmentOrders: null,
                        shippingDeadlineOrderCount: null,
                        isAnyShopHasAbnormalOrder: null,
                    },
                );

                this.totalLogisticsWarningOrders = totalLogisticsWarningOrders;
                this.totalOnHoldOrders = totalOnHoldOrders;
                this.totalRefundingOrders = totalRefundingOrders;
                this.totalPendingShipmentOrders = totalPendingShipmentOrders;
                this.shippingDeadlineOrderCount = shippingDeadlineOrderCount;
                this.isAnyShopHasAbnormalOrder = isAnyShopHasAbnormalOrder;
            }
        } catch (error) {
            console.log("loadShopOverview error:", error);
        }

        this.getShopOverviewLoading = false;
    }

    async loadWaybillStatsOverview() {
        try {
            const cached = this.getYesterdayStatsFromCache();
            if (
                this.hasYesterdayStatsCacheFields(cached, [
                    "yesterdayAllocatedQuantity",
                    "yesterdayCanceledQuantity",
                ])
            ) {
                this.yesterdayAllocatedQuantity = cached.yesterdayAllocatedQuantity;
                this.yesterdayCanceledQuantity = cached.yesterdayCanceledQuantity;
                return;
            }

            this.waybillStatLoading = true;

            const waybillStatsOverview = await getWaybillStatsOverview();

            if (!isEmpty(waybillStatsOverview.yesterdayAllocatedQuantity)) {
                this.yesterdayAllocatedQuantity = waybillStatsOverview.yesterdayAllocatedQuantity;
            } else {
                this.yesterdayAllocatedQuantity = null;
            }

            if (!isEmpty(waybillStatsOverview.yesterdayCanceledQuantity)) {
                this.yesterdayCanceledQuantity = waybillStatsOverview.yesterdayCanceledQuantity;
            } else {
                this.yesterdayCanceledQuantity = null;
            }

            this.setYesterdayStatsToCache({
                ...(this.getYesterdayStatsFromCache() || {}),
                yesterdayAllocatedQuantity: this.yesterdayAllocatedQuantity,
                yesterdayCanceledQuantity: this.yesterdayCanceledQuantity,
            });
        } catch (error) {
            console.log("loadWaybillStats error:", error);
        }

        this.waybillStatLoading = false;
    }

    async loadShipStatsOverview() {
        try {
            const cached = this.getYesterdayStatsFromCache();
            if (
                this.hasYesterdayStatsCacheFields(cached, [
                    "yesterdayOrderCount",
                    "yesterdayItemCount",
                    "yesterdayWaybillCount",
                ])
            ) {
                this.yesterdayOrderCount = cached.yesterdayOrderCount;
                this.yesterdayItemCount = cached.yesterdayItemCount;
                this.yesterdayWaybillCount = cached.yesterdayWaybillCount;
                return;
            }

            this.shipStatLoading = true;

            await this.ensureShopSetting();

            const dataSource: ShipmentStatsOverview[] = [];

            /** 筛选出高级版店铺的二维数组 */
            const advancedShopIdsList = this.shopIdsList
                .map((shopIds) =>
                    shopIds.filter(
                        (shopId) => this.globalStore.shopSettingMapping.get(shopId)?.appVersion === AppVersion.PREMIUM,
                    ),
                )
                .filter((row) => row.length > 0);

            await concurrentQueue({
                data: advancedShopIdsList,
                callback: async (shopIds) => {
                    const shipmentStatsOverview = await getShipStatsOverview(shopIds[0]);

                    dataSource.push(shipmentStatsOverview);
                },
            });

            const { yesterdayOrderCount, yesterdayItemCount, yesterdayWaybillCount } = dataSource.reduce(
                (total, current) => {
                    if (!isEmpty(current.yesterdayOrderCount)) {
                        if (!total.yesterdayOrderCount) {
                            total.yesterdayOrderCount = 0;
                        }

                        total.yesterdayOrderCount += current.yesterdayOrderCount;
                    }

                    if (!isEmpty(current.yesterdayItemCount)) {
                        if (!total.yesterdayItemCount) {
                            total.yesterdayItemCount = 0;
                        }

                        total.yesterdayItemCount += current.yesterdayItemCount;
                    }

                    if (!isEmpty(current.yesterdayWaybillCount)) {
                        if (!total.yesterdayWaybillCount) {
                            total.yesterdayWaybillCount = 0;
                        }

                        total.yesterdayWaybillCount += current.yesterdayWaybillCount;
                    }

                    return total;
                },
                {
                    yesterdayOrderCount: null,
                    yesterdayItemCount: null,
                    yesterdayWaybillCount: null,
                },
            );

            this.yesterdayOrderCount = yesterdayOrderCount;
            this.yesterdayItemCount = yesterdayItemCount;
            this.yesterdayWaybillCount = yesterdayWaybillCount;

            this.setYesterdayStatsToCache({
                ...(this.getYesterdayStatsFromCache() || {}),
                yesterdayOrderCount: this.yesterdayOrderCount,
                yesterdayItemCount: this.yesterdayItemCount,
                yesterdayWaybillCount: this.yesterdayWaybillCount,
            });
        } catch (error) {
            console.log("loadWaybillStats error:", error);
        }

        this.shipStatLoading = false;
    }

    async loadWmsOverview() {
        try {
            this.wmsLoading = true;

            const { inventoryWarningCount, anyInventoryThresholdConfigured } = await getInventoryWarningOverview();

            if (!isEmpty(inventoryWarningCount)) {
                this.inventoryWarningCount = inventoryWarningCount;
            } else {
                this.inventoryWarningCount = null;
            }

            this.anyInventoryThresholdConfigured = anyInventoryThresholdConfigured;
        } catch (error) {
            console.log("loadWmsOverview error:", error);
        }

        this.wmsLoading = false;
    }

    getCount(value) {
        if (value === undefined) return undefined;

        if (value === null) return this.NoStatistics;

        if (typeof value !== "number" && typeof value !== "string") return null;

        return value;
    }
}

export const ShopInfoStoreContext = React.createContext<ShopInfoStore>(null);

export const useShopInfoStore = () => React.useContext(ShopInfoStoreContext);

export default ShopInfoStore;
